/**
 * Spend guardrail expression (MD-5556).
 *
 * The operative target never changes shape: `targetMerType` ("amer" | "mer"),
 * `targetMerValue` (a ratio) and `targetMerScope`. Everything that spends
 * against the guardrail — the recommended daily budget, the Budget
 * Recommender, the nightly financial_targets_sync Lambda in connectly — reads
 * only those three fields.
 *
 * `targetMerDisplay` is how the customer *expresses* that target: aMER, MER,
 * or CAC. CAC and aMER are the same guardrail seen from two sides:
 *
 *   aMER = new-customer revenue / ad spend
 *   CAC  = ad spend / new customers
 *   ⇒ CAC = (new-customer revenue / new customers) / aMER
 *        = average first order value / aMER
 *
 * so a CAC target is stored as its equivalent aMER and converted back for
 * display with the company's current average first order value. MUD\WTR's
 * 0.70 aMER at a $47 first order is a $67 CAC.
 */
import { formatNumber } from "~/ui-rtk/utils/format"

export type TargetMerType = "amer" | "mer"
export type TargetMerScope = "ecomm" | "total"
export type TargetMerDisplay = "amer" | "mer" | "cac"

export const TARGET_MER_DISPLAYS: readonly TargetMerDisplay[] = [
  "amer",
  "mer",
  "cac",
]

/**
 * Decimal places kept on an aMER derived from a CAC target. Six keeps every
 * whole-dollar CAC reading back to the cent (four drifted: $150 became $150.02).
 */
const AMER_PRECISION = 6

function isTargetMerDisplay(value: unknown): value is TargetMerDisplay {
  return (TARGET_MER_DISPLAYS as readonly unknown[]).includes(value)
}

/**
 * The display a financials row expresses its guardrail in. Rows saved before
 * the column existed have no display and read as their operative type.
 */
export function resolveTargetMerDisplay(
  financials?: {
    targetMerDisplay?: string | null
    targetMerType?: string | null
  } | null,
): TargetMerDisplay {
  if (isTargetMerDisplay(financials?.targetMerDisplay)) {
    return financials.targetMerDisplay
  }
  return financials?.targetMerType === "mer" ? "mer" : "amer"
}

/** The operative type a display expresses: CAC is an aMER seen per customer. */
export function merTypeForDisplay(display: TargetMerDisplay): TargetMerType {
  return display === "mer" ? "mer" : "amer"
}

/**
 * aMER equivalent of a CAC target at the given average first order value.
 * 0 (an unset target) when either input is unusable.
 */
export function amerFromCac(
  cac: number,
  firstOrderValue: number | null,
): number {
  if (
    !Number.isFinite(cac) ||
    cac <= 0 ||
    firstOrderValue === null ||
    !(firstOrderValue > 0)
  ) {
    return 0
  }
  const factor = 10 ** AMER_PRECISION
  return Math.round((firstOrderValue / cac) * factor) / factor
}

/**
 * CAC equivalent of an aMER at the given average first order value.
 * null when the aMER is unset or the first order value is unknown.
 */
export function cacFromAmer(
  amer: number | null,
  firstOrderValue: number | null,
): number | null {
  if (
    amer === null ||
    !Number.isFinite(amer) ||
    amer <= 0 ||
    firstOrderValue === null ||
    !(firstOrderValue > 0)
  ) {
    return null
  }
  return firstOrderValue / amer
}

export function guardrailUnitLabel(display: TargetMerDisplay): string {
  if (display === "cac") return "CAC"
  if (display === "mer") return "MER"
  return "aMER"
}

export function guardrailScopeLabel(scope: TargetMerScope): string {
  return scope === "ecomm" ? "Ecomm" : "Total"
}

/** e.g. "aMER (Total)", "CAC (Ecomm)" — the label the Budget Recommender shows. */
export function guardrailLabel(
  display: TargetMerDisplay,
  scope: TargetMerScope,
): string {
  return `${guardrailUnitLabel(display)} (${guardrailScopeLabel(scope)})`
}

/** "0.70x" for a ratio display, "$67.00" for CAC. */
export function formatGuardrailValue(
  value: number,
  display: TargetMerDisplay,
): string {
  return display === "cac"
    ? formatNumber(value, { currency: true })
    : `${value.toFixed(2)}x`
}

/**
 * Actual vs. target for the guardrail as the Budget Recommender computes it:
 * both are ratios of the operative type, plus how the customer expresses it.
 */
export interface GuardrailPerformance {
  actualMer: number | null
  targetMer: number
  merLabel: string
  /** How the customer expresses the guardrail; ratio (aMER/MER) when absent. */
  targetMerDisplay?: TargetMerDisplay
  targetMerScope?: TargetMerScope
}

/** Actual vs. target in the customer's own guardrail terms. */
export interface GuardrailComparison {
  label: string
  actual: number | null
  target: number
  display: TargetMerDisplay
  /** CAC is a cost: under target is good. Ratios: over target is good. */
  lowerIsBetter: boolean
}

export function compareGuardrail(
  metrics: GuardrailPerformance,
  firstOrderValue: number | null | undefined,
): GuardrailComparison {
  const ratio: GuardrailComparison = {
    label: metrics.merLabel,
    actual: metrics.actualMer,
    target: metrics.targetMer,
    display: metrics.targetMerDisplay === "mer" ? "mer" : "amer",
    lowerIsBetter: false,
  }
  // A CAC display needs the first order value to convert; without it, fall
  // back to the stored ratio rather than show a mislabeled number.
  if (
    metrics.targetMerDisplay !== "cac" ||
    firstOrderValue === null ||
    firstOrderValue === undefined ||
    !(firstOrderValue > 0)
  ) {
    return ratio
  }
  return {
    label: guardrailLabel("cac", metrics.targetMerScope ?? "total"),
    actual: cacFromAmer(metrics.actualMer, firstOrderValue),
    target: cacFromAmer(metrics.targetMer, firstOrderValue) ?? 0,
    display: "cac",
    lowerIsBetter: true,
  }
}
